import { useCallback, useState } from "react";
import { message } from "antd";
import { useNavigate } from "react-router-dom";
import Utilities from "../../dependencies/custom/react-utilities";
import domainSettings from "../../dependencies/custom/settings";

const asList = (data) => (Array.isArray(data) ? data : data?.results || []);

export const usePayrollSetup = () => {
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [components, setComponents] = useState([]);
  const [structures, setStructures] = useState([]);
  const [scales, setScales] = useState([]);
  const [statutoryRules, setStatutoryRules] = useState([]);
  const [componentOverrides, setComponentOverrides] = useState([]);
  const [banks, setBanks] = useState([]);
  const [paymentAccounts, setPaymentAccounts] = useState([]);
  const [summary, setSummary] = useState({
    totalProfiles: 0,
    readyProfiles: 0,
    incompleteProfiles: 0,
    eligibleProfiles: 0,
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchProfiles = useCallback(
    async (filters = {}) => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (filters.search) params.set("search", filters.search);
        if (filters.departmentId)
          params.set("department_id", filters.departmentId);
        if (filters.readiness) params.set("readiness", filters.readiness);
        if (
          filters.payrollEligible !== undefined &&
          filters.payrollEligible !== null
        ) {
          params.set("payroll_eligible", filters.payrollEligible);
        }
        const query = params.toString();
        const url = `${domainSettings.urls.finance.payrollProfiles}${query ? `?${query}` : ""}`;
        const response = await Utilities.fetchWithAuth(
          "GET",
          url,
          null,
          navigate,
        );
        if (response.ok) setProfiles(asList(response.data));
        else
          Utilities.showError(
            response,
            "Failed to load employee payroll profiles",
          );
      } catch (error) {
        console.error(error);
        message.error("Failed to load employee payroll profiles");
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  const fetchSummary = useCallback(async () => {
    try {
      const response = await Utilities.fetchWithAuth(
        "GET",
        domainSettings.urls.finance.payrollProfilesSummary,
        null,
        navigate,
      );
      if (response.ok) setSummary(response.data);
    } catch (error) {
      console.error(error);
    }
  }, [navigate]);

  const fetchComponents = useCallback(async () => {
    const response = await Utilities.fetchWithAuth(
      "GET",
      domainSettings.urls.finance.payrollComponents,
      null,
      navigate,
    );
    if (response.ok) setComponents(asList(response.data));
    else Utilities.showError(response, "Failed to load payroll components");
  }, [navigate]);

  const fetchStructures = useCallback(async () => {
    const response = await Utilities.fetchWithAuth(
      "GET",
      domainSettings.urls.finance.payrollStructures,
      null,
      navigate,
    );
    if (response.ok) setStructures(asList(response.data));
    else Utilities.showError(response, "Failed to load salary structures");
  }, [navigate]);

  const fetchScales = useCallback(async () => {
    const response = await Utilities.fetchWithAuth(
      "GET",
      domainSettings.urls.finance.payrollScales,
      null,
      navigate,
    );
    if (response.ok) setScales(asList(response.data));
    else Utilities.showError(response, "Failed to load salary scales");
  }, [navigate]);

  const fetchStatutoryRules = useCallback(async () => {
    const response = await Utilities.fetchWithAuth(
      "GET",
      domainSettings.urls.finance.payrollStatutoryRules,
      null,
      navigate,
    );
    if (response.ok) setStatutoryRules(asList(response.data));
    else Utilities.showError(response, "Failed to load payroll rules");
  }, [navigate]);

  const fetchUgandaStatutoryDefaults = useCallback(async () => {
    const response = await Utilities.fetchWithAuth(
      "GET",
      domainSettings.urls.finance.payrollUgandaStatutoryDefaults,
      null,
      navigate,
    );
    if (!response.ok) {
      Utilities.showError(
        response,
        "Could not load the Uganda payroll defaults",
      );
      return null;
    }
    return response.data;
  }, [navigate]);

  const fetchComponentOverrides = useCallback(
    async (profileId) => {
      try {
        const params = profileId ? `?profile_id=${profileId}` : "";
        const response = await Utilities.fetchWithAuth(
          "GET",
          `${domainSettings.urls.finance.payrollComponentOverrides}${params}`,
          null,
          navigate,
        );
        if (response.ok) {
          const overrides = asList(response.data);
          setComponentOverrides(overrides);
          return overrides;
        }
        setComponentOverrides([]);
        Utilities.showError(
          response,
          "Failed to load employee component values",
        );
      } catch (error) {
        console.error(error);
        setComponentOverrides([]);
        message.error("Failed to load employee component values");
      }
      return [];
    },
    [navigate],
  );

  const fetchBanks = useCallback(async () => {
    const response = await Utilities.fetchWithAuth(
      "GET",
      domainSettings.urls.finance.payrollBanks,
      null,
      navigate,
    );
    if (response.ok) setBanks(asList(response.data));
    else Utilities.showError(response, "Failed to load payroll banks");
    return response.ok ? asList(response.data) : [];
  }, [navigate]);

  const fetchPaymentAccounts = useCallback(async () => {
    const response = await Utilities.fetchWithAuth(
      "GET",
      domainSettings.urls.finance.payrollPaymentAccounts,
      null,
      navigate,
    );
    if (response.ok) setPaymentAccounts(asList(response.data));
    else
      Utilities.showError(response, "Failed to load school payment accounts");
    return response.ok ? asList(response.data) : [];
  }, [navigate]);

  const refresh = useCallback(
    async (filters = {}) => {
      await Promise.all([
        fetchProfiles(filters),
        fetchSummary(),
        fetchComponents(),
        fetchStructures(),
        fetchScales(),
        fetchStatutoryRules(),
        fetchBanks(),
        fetchPaymentAccounts(),
      ]);
    },
    [
      fetchBanks,
      fetchComponents,
      fetchPaymentAccounts,
      fetchProfiles,
      fetchScales,
      fetchStatutoryRules,
      fetchStructures,
      fetchSummary,
    ],
  );

  const createEmployeeWithSalary = useCallback(
    async (profile, salary, allowances = []) => {
      setSaving(true);
      try {
        const response = await Utilities.fetchWithAuth(
          "POST",
          domainSettings.urls.finance.payrollCreateEmployeeWithSalary,
          { profile, salary, allowances },
          navigate,
        );
        if (!response.ok) {
          Utilities.showError(
            response,
            "Could not create the employee and initial salary",
          );
          return null;
        }
        message.success("Employee and initial salary created");
        return response.data;
      } catch (error) {
        console.error(error);
        message.error("Could not create the employee and initial salary");
        return null;
      } finally {
        setSaving(false);
      }
    },
    [navigate],
  );

  const saveProfile = useCallback(
    async (profileId, data) => {
      setSaving(true);
      try {
        const url = profileId
          ? domainSettings.urls.finance.payrollProfileDetail.replace(
              ":id",
              profileId,
            )
          : domainSettings.urls.finance.payrollProfiles;
        const response = await Utilities.fetchWithAuth(
          profileId ? "PATCH" : "POST",
          url,
          data,
          navigate,
        );
        if (!response.ok) {
          Utilities.showError(
            response,
            "Could not save the employee payroll profile",
          );
          return null;
        }
        message.success(
          profileId
            ? "Employee payroll profile updated"
            : "Employee added to payroll setup",
        );
        return response.data;
      } catch (error) {
        console.error(error);
        message.error("Could not save the employee payroll profile");
        return null;
      } finally {
        setSaving(false);
      }
    },
    [navigate],
  );

  const deleteProfile = useCallback(
    async (profileId) => {
      setSaving(true);
      try {
        const url = domainSettings.urls.finance.payrollProfileDetail.replace(
          ":id",
          profileId,
        );
        const response = await Utilities.fetchWithAuth(
          "DELETE",
          url,
          null,
          navigate,
        );
        if (!response.ok) {
          Utilities.showError(
            response,
            "Could not delete the employee payroll profile",
          );
          return false;
        }
        message.success("Employee removed from payroll setup");
        return true;
      } catch (error) {
        console.error(error);
        message.error("Could not delete the employee payroll profile");
        return false;
      } finally {
        setSaving(false);
      }
    },
    [navigate],
  );

  const saveSalary = useCallback(
    async (assignmentId, data) => {
      setSaving(true);
      try {
        const url = assignmentId
          ? domainSettings.urls.finance.payrollSalaryAssignmentDetail.replace(
              ":id",
              assignmentId,
            )
          : domainSettings.urls.finance.payrollSalaryAssignments;
        const response = await Utilities.fetchWithAuth(
          assignmentId ? "PATCH" : "POST",
          url,
          data,
          navigate,
        );
        if (!response.ok) {
          Utilities.showError(response, "Could not save the salary");
          return null;
        }
        const dateReconciliation = response.data?.dateReconciliation;
        if (dateReconciliation) {
          const reconciledCount =
            (dateReconciliation.removed?.length || 0) +
            (dateReconciliation.trimmed?.length || 0) +
            (dateReconciliation.shifted?.length || 0) +
            (dateReconciliation.split?.length || 0);
          message.success(
            reconciledCount
              ? `Salary dates corrected; ${reconciledCount} overlapping record${reconciledCount === 1 ? " was" : "s were"} reconciled`
              : "Salary dates corrected",
          );
        } else {
          message.success(
            assignmentId ? "Salary record updated" : "Salary assigned",
          );
        }
        return response.data;
      } catch (error) {
        console.error(error);
        message.error("Could not save the salary");
        return null;
      } finally {
        setSaving(false);
      }
    },
    [navigate],
  );

  const fetchSalaryAssignments = useCallback(
    async (profileId) => {
      try {
        const params = profileId ? `?profile_id=${profileId}` : "";
        const response = await Utilities.fetchWithAuth(
          "GET",
          `${domainSettings.urls.finance.payrollSalaryAssignments}${params}`,
          null,
          navigate,
        );
        if (!response.ok) {
          Utilities.showError(response, "Could not load salary history");
          return [];
        }
        return asList(response.data);
      } catch (error) {
        console.error(error);
        message.error("Could not load salary history");
        return [];
      }
    },
    [navigate],
  );

  const deleteSalary = useCallback(
    async (assignmentId) => {
      setSaving(true);
      try {
        const url =
          domainSettings.urls.finance.payrollSalaryAssignmentDetail.replace(
            ":id",
            assignmentId,
          );
        const response = await Utilities.fetchWithAuth(
          "DELETE",
          url,
          null,
          navigate,
        );
        if (!response.ok) {
          Utilities.showError(response, "Could not delete the salary record");
          return false;
        }
        message.success("Unused salary record deleted");
        return true;
      } catch (error) {
        console.error(error);
        message.error("Could not delete the salary record");
        return false;
      } finally {
        setSaving(false);
      }
    },
    [navigate],
  );

  const reviseSalary = useCallback(
    async (assignmentId, data) => {
      setSaving(true);
      try {
        const url =
          domainSettings.urls.finance.payrollSalaryAssignmentRevise.replace(
            ":id",
            assignmentId,
          );
        const response = await Utilities.fetchWithAuth(
          "POST",
          url,
          data,
          navigate,
        );
        if (!response.ok) {
          Utilities.showError(response, "Could not revise the salary");
          return null;
        }
        message.success(
          "Salary revision created and the previous salary was preserved",
        );
        return response.data;
      } catch (error) {
        console.error(error);
        message.error("Could not revise the salary");
        return null;
      } finally {
        setSaving(false);
      }
    },
    [navigate],
  );

  const saveComponent = useCallback(
    async (componentId, data) => {
      setSaving(true);
      try {
        const url = componentId
          ? domainSettings.urls.finance.payrollComponentDetail.replace(
              ":id",
              componentId,
            )
          : domainSettings.urls.finance.payrollComponents;
        const response = await Utilities.fetchWithAuth(
          componentId ? "PATCH" : "POST",
          url,
          data,
          navigate,
        );
        if (!response.ok) {
          Utilities.showError(response, "Could not save the payroll component");
          return null;
        }
        message.success(
          componentId
            ? "Payroll component updated"
            : "Payroll component created",
        );
        await fetchComponents();
        return response.data;
      } catch (error) {
        console.error(error);
        message.error("Could not save the payroll component");
        return null;
      } finally {
        setSaving(false);
      }
    },
    [fetchComponents, navigate],
  );

  const deleteComponent = useCallback(
    async (componentId) => {
      setSaving(true);
      try {
        const url = domainSettings.urls.finance.payrollComponentDetail.replace(
          ":id",
          componentId,
        );
        const response = await Utilities.fetchWithAuth(
          "DELETE",
          url,
          null,
          navigate,
        );
        if (!response.ok) {
          Utilities.showError(
            response,
            "Cannot delete this component because this code or one of its versions is used by payroll. Keep it inactive or end-dated instead.",
          );
          return false;
        }
        message.success("Payroll component deleted");
        await fetchComponents();
        return true;
      } catch (error) {
        console.error(error);
        message.error("Could not delete the payroll component");
        return false;
      } finally {
        setSaving(false);
      }
    },
    [fetchComponents, navigate],
  );

  const saveStructure = useCallback(
    async (structureId, data) => {
      setSaving(true);
      try {
        const url = structureId
          ? domainSettings.urls.finance.payrollStructureDetail.replace(
              ":id",
              structureId,
            )
          : domainSettings.urls.finance.payrollStructures;
        const response = await Utilities.fetchWithAuth(
          structureId ? "PATCH" : "POST",
          url,
          data,
          navigate,
        );
        if (!response.ok) {
          Utilities.showError(response, "Could not save the salary structure");
          return null;
        }
        message.success(
          structureId ? "Salary structure updated" : "Salary structure created",
        );
        await fetchStructures();
        return response.data;
      } catch (error) {
        console.error(error);
        message.error("Could not save the salary structure");
        return null;
      } finally {
        setSaving(false);
      }
    },
    [fetchStructures, navigate],
  );

  const deleteStructure = useCallback(
    async (structureId) => {
      setSaving(true);
      try {
        const url = domainSettings.urls.finance.payrollStructureDetail.replace(
          ":id",
          structureId,
        );
        const response = await Utilities.fetchWithAuth(
          "DELETE",
          url,
          null,
          navigate,
        );
        if (!response.ok) {
          Utilities.showError(
            response,
            "Could not delete the salary structure",
          );
          return false;
        }
        message.success("Salary structure deleted");
        await fetchStructures();
        return true;
      } catch (error) {
        console.error(error);
        message.error("Could not delete the salary structure");
        return false;
      } finally {
        setSaving(false);
      }
    },
    [fetchStructures, navigate],
  );

  const saveScale = useCallback(
    async (scaleId, data) => {
      setSaving(true);
      try {
        const url = scaleId
          ? domainSettings.urls.finance.payrollScaleDetail.replace(
              ":id",
              scaleId,
            )
          : domainSettings.urls.finance.payrollScales;
        const response = await Utilities.fetchWithAuth(
          scaleId ? "PATCH" : "POST",
          url,
          data,
          navigate,
        );
        if (!response.ok) {
          Utilities.showError(response, "Could not save the salary scale");
          return null;
        }
        message.success(
          scaleId ? "Salary scale updated" : "Salary scale created",
        );
        await fetchScales();
        return response.data;
      } catch (error) {
        console.error(error);
        message.error("Could not save the salary scale");
        return null;
      } finally {
        setSaving(false);
      }
    },
    [fetchScales, navigate],
  );

  const deleteScale = useCallback(
    async (scaleId) => {
      setSaving(true);
      try {
        const url = domainSettings.urls.finance.payrollScaleDetail.replace(
          ":id",
          scaleId,
        );
        const response = await Utilities.fetchWithAuth(
          "DELETE",
          url,
          null,
          navigate,
        );
        if (!response.ok) {
          Utilities.showError(response, "Could not delete the salary scale");
          return false;
        }
        message.success("Salary scale deleted");
        await fetchScales();
        return true;
      } catch (error) {
        console.error(error);
        message.error("Could not delete the salary scale");
        return false;
      } finally {
        setSaving(false);
      }
    },
    [fetchScales, navigate],
  );

  const saveStatutoryRule = useCallback(
    async (ruleSetId, data) => {
      setSaving(true);
      try {
        const url = ruleSetId
          ? domainSettings.urls.finance.payrollStatutoryRuleDetail.replace(
              ":id",
              ruleSetId,
            )
          : domainSettings.urls.finance.payrollStatutoryRules;
        const response = await Utilities.fetchWithAuth(
          ruleSetId ? "PATCH" : "POST",
          url,
          data,
          navigate,
        );
        if (!response.ok) {
          Utilities.showError(response, "Could not save the payroll rules");
          return null;
        }
        message.success(
          ruleSetId ? "Payroll rules updated" : "Payroll rules created",
        );
        await fetchStatutoryRules();
        return response.data;
      } catch (error) {
        console.error(error);
        message.error("Could not save the payroll rules");
        return null;
      } finally {
        setSaving(false);
      }
    },
    [fetchStatutoryRules, navigate],
  );

  const deleteStatutoryRule = useCallback(
    async (ruleSetId) => {
      setSaving(true);
      try {
        const url =
          domainSettings.urls.finance.payrollStatutoryRuleDetail.replace(
            ":id",
            ruleSetId,
          );
        const response = await Utilities.fetchWithAuth(
          "DELETE",
          url,
          null,
          navigate,
        );
        if (!response.ok) {
          Utilities.showError(response, "Could not delete the payroll rules");
          return false;
        }
        message.success("Payroll rules deleted");
        await fetchStatutoryRules();
        return true;
      } catch (error) {
        console.error(error);
        message.error("Could not delete the payroll rules");
        return false;
      } finally {
        setSaving(false);
      }
    },
    [fetchStatutoryRules, navigate],
  );

  const copySetupVersion = useCallback(
    async (kind, id, data) => {
      const endpoints = {
        component: domainSettings.urls.finance.payrollComponentCopyVersion,
        structure: domainSettings.urls.finance.payrollStructureCopyVersion,
        scale: domainSettings.urls.finance.payrollScaleCopyVersion,
        rule: domainSettings.urls.finance.payrollStatutoryRuleCopyVersion,
      };
      const refreshers = {
        component: fetchComponents,
        structure: fetchStructures,
        scale: fetchScales,
        rule: fetchStatutoryRules,
      };
      setSaving(true);
      try {
        const response = await Utilities.fetchWithAuth(
          "POST",
          endpoints[kind].replace(":id", id),
          data,
          navigate,
        );
        if (!response.ok) {
          Utilities.showError(response, "Could not create the new version");
          return null;
        }
        message.success(
          "New version created; the earlier version was preserved",
        );
        await refreshers[kind]();
        return response.data;
      } finally {
        setSaving(false);
      }
    },
    [
      fetchComponents,
      fetchScales,
      fetchStatutoryRules,
      fetchStructures,
      navigate,
    ],
  );

  const saveComponentOverride = useCallback(
    async (overrideId, data) => {
      setSaving(true);
      try {
        const url = overrideId
          ? domainSettings.urls.finance.payrollComponentOverrideDetail.replace(
              ":id",
              overrideId,
            )
          : domainSettings.urls.finance.payrollComponentOverrides;
        const response = await Utilities.fetchWithAuth(
          overrideId ? "PATCH" : "POST",
          url,
          data,
          navigate,
        );
        if (!response.ok) {
          Utilities.showError(
            response,
            "Could not save the employee component value",
          );
          return null;
        }
        message.success("Employee component value saved");
        await fetchComponentOverrides(data.profileId);
        return response.data;
      } finally {
        setSaving(false);
      }
    },
    [fetchComponentOverrides, navigate],
  );

  const deleteComponentOverride = useCallback(
    async (overrideId, profileId) => {
      setSaving(true);
      try {
        const url =
          domainSettings.urls.finance.payrollComponentOverrideDetail.replace(
            ":id",
            overrideId,
          );
        const response = await Utilities.fetchWithAuth(
          "DELETE",
          url,
          null,
          navigate,
        );
        if (!response.ok) {
          Utilities.showError(
            response,
            "Could not delete the employee component value",
          );
          return false;
        }
        message.success("Employee component value deleted");
        await fetchComponentOverrides(profileId);
        return true;
      } finally {
        setSaving(false);
      }
    },
    [fetchComponentOverrides, navigate],
  );

  const saveBank = useCallback(
    async (bankId, data) => {
      setSaving(true);
      try {
        const url = bankId
          ? domainSettings.urls.finance.payrollBankDetail.replace(":id", bankId)
          : domainSettings.urls.finance.payrollBanks;
        const response = await Utilities.fetchWithAuth(
          bankId ? "PATCH" : "POST",
          url,
          data,
          navigate,
        );
        if (!response.ok) {
          Utilities.showError(response, "Could not save the bank");
          return null;
        }
        message.success(bankId ? "Bank updated" : "Bank created");
        await fetchBanks();
        return response.data;
      } finally {
        setSaving(false);
      }
    },
    [fetchBanks, navigate],
  );

  const deleteBank = useCallback(
    async (bankId) => {
      setSaving(true);
      try {
        const response = await Utilities.fetchWithAuth(
          "DELETE",
          domainSettings.urls.finance.payrollBankDetail.replace(":id", bankId),
          null,
          navigate,
        );
        if (!response.ok) {
          Utilities.showError(response, "Could not delete the bank");
          return false;
        }
        message.success("Bank deleted");
        await fetchBanks();
        return true;
      } finally {
        setSaving(false);
      }
    },
    [fetchBanks, navigate],
  );

  const savePaymentAccount = useCallback(
    async (accountId, data) => {
      setSaving(true);
      try {
        const url = accountId
          ? domainSettings.urls.finance.payrollPaymentAccountDetail.replace(
              ":id",
              accountId,
            )
          : domainSettings.urls.finance.payrollPaymentAccounts;
        const response = await Utilities.fetchWithAuth(
          accountId ? "PATCH" : "POST",
          url,
          data,
          navigate,
        );
        if (!response.ok) {
          Utilities.showError(
            response,
            "Could not save the school payment account",
          );
          return null;
        }
        message.success(
          accountId ? "Payment account updated" : "Payment account created",
        );
        await fetchPaymentAccounts();
        return response.data;
      } finally {
        setSaving(false);
      }
    },
    [fetchPaymentAccounts, navigate],
  );

  const deletePaymentAccount = useCallback(
    async (accountId) => {
      setSaving(true);
      try {
        const response = await Utilities.fetchWithAuth(
          "DELETE",
          domainSettings.urls.finance.payrollPaymentAccountDetail.replace(
            ":id",
            accountId,
          ),
          null,
          navigate,
        );
        if (!response.ok) {
          Utilities.showError(
            response,
            "Could not delete the school payment account",
          );
          return false;
        }
        message.success("Payment account deleted");
        await fetchPaymentAccounts();
        return true;
      } finally {
        setSaving(false);
      }
    },
    [fetchPaymentAccounts, navigate],
  );

  const setDefaultPaymentAccount = useCallback(
    async (accountId) => {
      setSaving(true);
      try {
        const response = await Utilities.fetchWithAuth(
          "POST",
          domainSettings.urls.finance.payrollPaymentAccountSetDefault.replace(
            ":id",
            accountId,
          ),
          {},
          navigate,
        );
        if (!response.ok) {
          Utilities.showError(
            response,
            "Could not set the default payment account",
          );
          return false;
        }
        message.success("Default payroll payment account updated");
        await fetchPaymentAccounts();
        return true;
      } finally {
        setSaving(false);
      }
    },
    [fetchPaymentAccounts, navigate],
  );

  const fetchEmployeeBankHistory = useCallback(
    async (profileId) => {
      const response = await Utilities.fetchWithAuth(
        "GET",
        domainSettings.urls.finance.payrollEmployeeBankHistory.replace(
          ":id",
          profileId,
        ),
        null,
        navigate,
      );
      if (!response.ok) {
        Utilities.showError(response, "Could not load employee bank history");
        return [];
      }
      return asList(response.data);
    },
    [navigate],
  );

  const runRuleAction = useCallback(
    async (ruleSetId, action, data = {}) => {
      const endpoints = {
        simulate: domainSettings.urls.finance.payrollStatutoryRuleSimulate,
        runTests: domainSettings.urls.finance.payrollStatutoryRuleRunTests,
        markTested: domainSettings.urls.finance.payrollStatutoryRuleMarkTested,
        approve: domainSettings.urls.finance.payrollStatutoryRuleApprove,
        activate: domainSettings.urls.finance.payrollStatutoryRuleActivate,
        retire: domainSettings.urls.finance.payrollStatutoryRuleRetire,
      };
      setSaving(true);
      try {
        const response = await Utilities.fetchWithAuth(
          "POST",
          endpoints[action].replace(":id", ruleSetId),
          data,
          navigate,
        );
        if (!response.ok) {
          Utilities.showError(
            response,
            "Payroll rule action could not be completed",
          );
          return null;
        }
        if (action !== "simulate") {
          message.success(
            action === "runTests"
              ? "Rule tests completed"
              : "Rule status updated",
          );
          await fetchStatutoryRules();
        }
        return response.data;
      } finally {
        setSaving(false);
      }
    },
    [fetchStatutoryRules, navigate],
  );

  const testDraftStatutoryRule = useCallback(
    async (data) => {
      setSaving(true);
      try {
        const response = await Utilities.fetchWithAuth(
          "POST",
          domainSettings.urls.finance.payrollStatutoryRuleTestDraft,
          data,
          navigate,
        );
        if (!response.ok) {
          return {
            ok: false,
            detail:
              response.data?.detail ||
              "Complete the rule configuration before testing.",
            errors: response.data?.errors || response.data || {},
          };
        }
        return { ok: true, ...response.data };
      } finally {
        setSaving(false);
      }
    },
    [navigate],
  );

  return {
    profiles,
    components,
    structures,
    scales,
    statutoryRules,
    componentOverrides,
    banks,
    paymentAccounts,
    summary,
    loading,
    saving,
    fetchProfiles,
    refresh,
    saveProfile,
    deleteProfile,
    createEmployeeWithSalary,
    saveSalary,
    fetchSalaryAssignments,
    deleteSalary,
    reviseSalary,
    saveComponent,
    deleteComponent,
    saveStructure,
    deleteStructure,
    saveScale,
    deleteScale,
    fetchUgandaStatutoryDefaults,
    saveStatutoryRule,
    deleteStatutoryRule,
    fetchComponentOverrides,
    fetchBanks,
    fetchPaymentAccounts,
    fetchEmployeeBankHistory,
    saveComponentOverride,
    deleteComponentOverride,
    saveBank,
    deleteBank,
    savePaymentAccount,
    deletePaymentAccount,
    setDefaultPaymentAccount,
    copySetupVersion,
    runRuleAction,
    testDraftStatutoryRule,
  };
};

export default usePayrollSetup;
